import { z } from "zod";
import {
  commandContextSchema,
  commandError,
  stableFingerprint,
  type AuditEntry,
  type CommandContext,
  type CommandReceipt,
  type CommandResult,
  type CommandSuccess,
} from "../../lib/command-contract";
import {
  definitionUsageCount,
  normalizeClassificationName,
  optionUsageCount,
  uniqueClassificationKey,
  type ClassificationDefinition,
  type ClassificationOption,
  type LeadClassificationValue,
} from "./contracts";

/**
 * Comandos das classificações, sobre o contrato comum de comando.
 *
 * A regra que organiza o módulo: item COM histórico é arquivado, item SEM
 * histórico pode ser apagado, e apagar à força exige confirmação nominal na
 * interface e diz antes o que será perdido (§18.1). Nenhum comando aqui apaga
 * em cascata silenciosa.
 */

export interface ClassificationCommandState {
  definitions: ClassificationDefinition[];
  options: ClassificationOption[];
  values: LeadClassificationValue[];
  commandReceipts: CommandReceipt[];
  auditLog: AuditEntry[];
  leadIds?: readonly string[];
}

export interface ClassificationCommandValue {
  definition?: ClassificationDefinition;
  option?: ClassificationOption;
  value?: LeadClassificationValue;
  /** Quantos leads referenciavam o item removido ou arquivado. */
  affectedLeads?: number;
}

export interface ClassificationCommandDeps {
  now: () => string;
  uuid: () => string;
}

export interface ClassificationCommandExecution {
  result: CommandResult<ClassificationCommandValue>;
  state: ClassificationCommandState;
}

const INVALID_CONTEXT: CommandContext = Object.freeze({
  ownerId: "invalid-context",
  actorType: "system",
  actorId: "classification-command",
  origin: "system",
  commandId: "invalid-context",
  correlationId: "invalid-context",
  idempotencyKey: "invalid-context",
  requestedAt: "1970-01-01T00:00:00.000Z",
  schemaVersion: 1,
});

const nameSchema = z.string().trim().min(1, "Informe um nome.").max(120);

export const createDefinitionSchema = z.object({ name: nameSchema });
export const renameDefinitionSchema = z.object({
  definitionId: z.string().trim().min(1),
  name: nameSchema,
});
export const archiveDefinitionSchema = z.object({
  definitionId: z.string().trim().min(1),
  archived: z.boolean(),
});
export const removeDefinitionSchema = z.object({
  definitionId: z.string().trim().min(1),
  /** Confirma apagar mesmo com leads usando a classificação. */
  force: z.boolean().default(false),
});
export const createOptionSchema = z.object({
  definitionId: z.string().trim().min(1),
  name: nameSchema,
});
export const renameOptionSchema = z.object({
  optionId: z.string().trim().min(1),
  name: nameSchema,
});
export const archiveOptionSchema = z.object({
  optionId: z.string().trim().min(1),
  archived: z.boolean(),
});
export const removeOptionSchema = z.object({
  optionId: z.string().trim().min(1),
  force: z.boolean().default(false),
});
export const reorderSchema = z.object({
  definitionId: z.string().trim().min(1).optional(),
  orderedIds: z.array(z.string().trim().min(1)).min(1),
});
export const setLeadValueSchema = z.object({
  leadId: z.string().trim().min(1),
  definitionId: z.string().trim().min(1),
  /** `null` remove o valor do lead. */
  optionId: z.string().trim().min(1).nullable(),
});

export type CreateDefinitionInput = z.input<typeof createDefinitionSchema>;
export type RenameDefinitionInput = z.input<typeof renameDefinitionSchema>;
export type ArchiveDefinitionInput = z.input<typeof archiveDefinitionSchema>;
export type RemoveDefinitionInput = z.input<typeof removeDefinitionSchema>;
export type CreateOptionInput = z.input<typeof createOptionSchema>;
export type RenameOptionInput = z.input<typeof renameOptionSchema>;
export type ArchiveOptionInput = z.input<typeof archiveOptionSchema>;
export type RemoveOptionInput = z.input<typeof removeOptionSchema>;
export type ReorderInput = z.input<typeof reorderSchema>;
export type SetLeadValueInput = z.input<typeof setLeadValueSchema>;

function cloneState(state: ClassificationCommandState): ClassificationCommandState {
  return {
    definitions: [...state.definitions],
    options: [...state.options],
    values: [...state.values],
    commandReceipts: [...state.commandReceipts],
    auditLog: [...state.auditLog],
    leadIds: state.leadIds,
  };
}

function checkedContext(context: unknown): CommandContext | undefined {
  const parsed = commandContextSchema.safeParse(context);
  return parsed.success ? parsed.data : undefined;
}

function fail(
  state: ClassificationCommandState,
  context: CommandContext,
  code: Parameters<typeof commandError>[1],
  message: string,
): ClassificationCommandExecution {
  return {
    state,
    result: commandError(context, code, message, {
      severity: code === "persistence_error" ? "error" : "warning",
    }),
  };
}

function replayLookup(
  state: ClassificationCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
): ClassificationCommandExecution | null {
  const receipt = state.commandReceipts.find(
    (item) =>
      item.ownerId === context.ownerId &&
      item.command === command &&
      item.idempotencyKey === context.idempotencyKey,
  );
  if (!receipt) return null;
  if (receipt.requestFingerprint !== fingerprint)
    return fail(
      state,
      context,
      "idempotency_conflict",
      "A chave de idempotência já foi usada para outra intenção.",
    );
  if (!receipt.result.ok)
    return { state, result: receipt.result as CommandResult<ClassificationCommandValue> };
  return {
    state,
    result: {
      ...(receipt.result as CommandSuccess<ClassificationCommandValue>),
      status: "replayed",
    },
  };
}

interface FinishInput {
  state: ClassificationCommandState;
  context: CommandContext;
  command: string;
  fingerprint: string;
  targetType: string;
  targetId: string;
  value: ClassificationCommandValue;
  nextState: ClassificationCommandState;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  deps: ClassificationCommandDeps;
}

function finish(input: FinishInput): ClassificationCommandExecution {
  const occurredAt = input.deps.now();
  const auditId = input.deps.uuid();
  const result: CommandSuccess<ClassificationCommandValue> = {
    ok: true,
    status: "applied",
    value: input.value,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    version: 1,
    eventIds: [],
    outboxJobIds: [],
    auditId,
    warnings: [],
  };
  const audit: AuditEntry = {
    id: auditId,
    ownerId: input.context.ownerId,
    command: input.command,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    causationId: input.context.causationId,
    actorType: input.context.actorType,
    actorId: input.context.actorId,
    origin: input.context.origin,
    targetType: input.targetType,
    targetId: input.targetId,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    result: "applied",
    occurredAt,
    before: input.before,
    after: input.after,
  };
  const receipt: CommandReceipt = {
    id: input.deps.uuid(),
    ownerId: input.context.ownerId,
    command: input.command,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    result,
    createdAt: occurredAt,
    schemaVersion: input.context.schemaVersion,
  };
  return {
    result,
    state: {
      ...input.nextState,
      auditLog: [...input.nextState.auditLog, audit],
      commandReceipts: [...input.nextState.commandReceipts, receipt],
    },
  };
}

function validationMessage(error: unknown): string {
  if (error instanceof z.ZodError) return error.issues[0]?.message ?? "Dados inválidos.";
  return error instanceof Error ? error.message : "Dados inválidos.";
}

function nextOrder(items: readonly { order: number }[]): number {
  return items.reduce((max, item) => Math.max(max, item.order), 0) + 1;
}

export function executeCreateClassification(
  stateInput: ClassificationCommandState,
  input: CreateDefinitionInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof createDefinitionSchema>;
  try {
    draft = createDefinitionSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.create", fingerprint);
  if (replay) return replay;

  const normalized = normalizeClassificationName(draft.name);
  if (state.definitions.some((item) => normalizeClassificationName(item.name) === normalized))
    return fail(stateInput, context, "invalid_state", "Já existe uma classificação com este nome.");

  const definition: ClassificationDefinition = {
    id: deps.uuid(),
    name: draft.name,
    key: uniqueClassificationKey(
      draft.name,
      state.definitions.map((item) => item.key),
    ),
    order: nextOrder(state.definitions),
  };
  return finish({
    state,
    context,
    command: "classifications.create",
    fingerprint,
    targetType: "classification_definition",
    targetId: definition.id,
    value: { definition },
    nextState: { ...state, definitions: [...state.definitions, definition] },
    after: { ...definition },
    deps,
  });
}

export function executeRenameClassification(
  stateInput: ClassificationCommandState,
  input: RenameDefinitionInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof renameDefinitionSchema>;
  try {
    draft = renameDefinitionSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.rename", fingerprint);
  if (replay) return replay;

  const current = state.definitions.find((item) => item.id === draft.definitionId);
  if (!current) return fail(stateInput, context, "not_found", "Classificação não encontrada.");
  const normalized = normalizeClassificationName(draft.name);
  if (
    state.definitions.some(
      (item) => item.id !== current.id && normalizeClassificationName(item.name) === normalized,
    )
  )
    return fail(stateInput, context, "invalid_state", "Já existe uma classificação com este nome.");

  // A chave NÃO muda: filtros salvos e exportações antigas apontam para ela.
  const definition: ClassificationDefinition = { ...current, name: draft.name };
  return finish({
    state,
    context,
    command: "classifications.rename",
    fingerprint,
    targetType: "classification_definition",
    targetId: definition.id,
    value: { definition },
    nextState: {
      ...state,
      definitions: state.definitions.map((item) => (item.id === definition.id ? definition : item)),
    },
    before: { ...current },
    after: { ...definition },
    deps,
  });
}

export function executeArchiveClassification(
  stateInput: ClassificationCommandState,
  input: ArchiveDefinitionInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof archiveDefinitionSchema>;
  try {
    draft = archiveDefinitionSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.archive", fingerprint);
  if (replay) return replay;

  const current = state.definitions.find((item) => item.id === draft.definitionId);
  if (!current) return fail(stateInput, context, "not_found", "Classificação não encontrada.");
  if (Boolean(current.archived) === draft.archived)
    return fail(
      stateInput,
      context,
      "invalid_state",
      draft.archived ? "A classificação já está arquivada." : "A classificação já está ativa.",
    );

  const definition: ClassificationDefinition = { ...current, archived: draft.archived };
  return finish({
    state,
    context,
    command: "classifications.archive",
    fingerprint,
    targetType: "classification_definition",
    targetId: definition.id,
    value: { definition, affectedLeads: definitionUsageCount(state.values, definition.id) },
    // Arquivar tira dos seletores ativos e PRESERVA os valores já gravados:
    // o histórico do lead continua legível (§18.1).
    nextState: {
      ...state,
      definitions: state.definitions.map((item) => (item.id === definition.id ? definition : item)),
    },
    before: { ...current },
    after: { ...definition },
    deps,
  });
}

export function executeRemoveClassification(
  stateInput: ClassificationCommandState,
  input: RemoveDefinitionInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof removeDefinitionSchema>;
  try {
    draft = removeDefinitionSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.remove", fingerprint);
  if (replay) return replay;

  const current = state.definitions.find((item) => item.id === draft.definitionId);
  if (!current) return fail(stateInput, context, "not_found", "Classificação não encontrada.");
  const affectedLeads = definitionUsageCount(state.values, current.id);
  // Sem confirmação explícita, item com histórico não é apagado: a interface
  // precisa dizer quantos leads perdem o dado antes de perguntar de novo.
  if (affectedLeads > 0 && !draft.force)
    return fail(
      stateInput,
      context,
      "invalid_state",
      `${affectedLeads} lead(s) usam esta classificação. Arquive ou confirme a exclusão permanente.`,
    );

  return finish({
    state,
    context,
    command: "classifications.remove",
    fingerprint,
    targetType: "classification_definition",
    targetId: current.id,
    value: { definition: current, affectedLeads },
    nextState: {
      ...state,
      definitions: state.definitions.filter((item) => item.id !== current.id),
      options: state.options.filter((item) => item.definitionId !== current.id),
      values: state.values.filter((item) => item.definitionId !== current.id),
    },
    before: { ...current, affectedLeads },
    deps,
  });
}

export function executeCreateClassificationOption(
  stateInput: ClassificationCommandState,
  input: CreateOptionInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof createOptionSchema>;
  try {
    draft = createOptionSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.option.create", fingerprint);
  if (replay) return replay;

  const definition = state.definitions.find((item) => item.id === draft.definitionId);
  if (!definition) return fail(stateInput, context, "not_found", "Classificação não encontrada.");
  if (definition.archived)
    return fail(stateInput, context, "invalid_state", "Classificação arquivada não recebe opção.");
  const normalized = normalizeClassificationName(draft.name);
  const siblings = state.options.filter((item) => item.definitionId === definition.id);
  if (siblings.some((item) => normalizeClassificationName(item.name) === normalized))
    return fail(stateInput, context, "invalid_state", "Já existe uma opção com este nome.");

  const option: ClassificationOption = {
    id: deps.uuid(),
    definitionId: definition.id,
    name: draft.name,
    order: nextOrder(siblings),
  };
  return finish({
    state,
    context,
    command: "classifications.option.create",
    fingerprint,
    targetType: "classification_option",
    targetId: option.id,
    value: { definition, option },
    nextState: { ...state, options: [...state.options, option] },
    after: { ...option },
    deps,
  });
}

export function executeRenameClassificationOption(
  stateInput: ClassificationCommandState,
  input: RenameOptionInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof renameOptionSchema>;
  try {
    draft = renameOptionSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.option.rename", fingerprint);
  if (replay) return replay;

  const current = state.options.find((item) => item.id === draft.optionId);
  if (!current) return fail(stateInput, context, "not_found", "Opção não encontrada.");
  const normalized = normalizeClassificationName(draft.name);
  if (
    state.options.some(
      (item) =>
        item.definitionId === current.definitionId &&
        item.id !== current.id &&
        normalizeClassificationName(item.name) === normalized,
    )
  )
    return fail(stateInput, context, "invalid_state", "Já existe uma opção com este nome.");

  // Renomear NÃO reescreve o passado: os leads continuam apontando para o mesmo
  // id de opção, e relatórios antigos não mudam de recorte (§10).
  const option: ClassificationOption = { ...current, name: draft.name };
  return finish({
    state,
    context,
    command: "classifications.option.rename",
    fingerprint,
    targetType: "classification_option",
    targetId: option.id,
    value: { option },
    nextState: {
      ...state,
      options: state.options.map((item) => (item.id === option.id ? option : item)),
    },
    before: { ...current },
    after: { ...option },
    deps,
  });
}

export function executeArchiveClassificationOption(
  stateInput: ClassificationCommandState,
  input: ArchiveOptionInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof archiveOptionSchema>;
  try {
    draft = archiveOptionSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.option.archive", fingerprint);
  if (replay) return replay;

  const current = state.options.find((item) => item.id === draft.optionId);
  if (!current) return fail(stateInput, context, "not_found", "Opção não encontrada.");
  if (Boolean(current.archived) === draft.archived)
    return fail(
      stateInput,
      context,
      "invalid_state",
      draft.archived ? "A opção já está arquivada." : "A opção já está ativa.",
    );

  const option: ClassificationOption = { ...current, archived: draft.archived };
  return finish({
    state,
    context,
    command: "classifications.option.archive",
    fingerprint,
    targetType: "classification_option",
    targetId: option.id,
    value: { option, affectedLeads: optionUsageCount(state.values, option.id) },
    nextState: {
      ...state,
      options: state.options.map((item) => (item.id === option.id ? option : item)),
    },
    before: { ...current },
    after: { ...option },
    deps,
  });
}

export function executeRemoveClassificationOption(
  stateInput: ClassificationCommandState,
  input: RemoveOptionInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof removeOptionSchema>;
  try {
    draft = removeOptionSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.option.remove", fingerprint);
  if (replay) return replay;

  const current = state.options.find((item) => item.id === draft.optionId);
  if (!current) return fail(stateInput, context, "not_found", "Opção não encontrada.");
  const affectedLeads = optionUsageCount(state.values, current.id);
  if (affectedLeads > 0 && !draft.force)
    return fail(
      stateInput,
      context,
      "invalid_state",
      `${affectedLeads} lead(s) usam esta opção. Arquive ou confirme a exclusão permanente.`,
    );

  return finish({
    state,
    context,
    command: "classifications.option.remove",
    fingerprint,
    targetType: "classification_option",
    targetId: current.id,
    value: { option: current, affectedLeads },
    nextState: {
      ...state,
      options: state.options.filter((item) => item.id !== current.id),
      values: state.values.filter((item) => item.optionId !== current.id),
    },
    before: { ...current, affectedLeads },
    deps,
  });
}

/** Reordena definições (sem `definitionId`) ou as opções de uma definição. */
export function executeReorderClassifications(
  stateInput: ClassificationCommandState,
  input: ReorderInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof reorderSchema>;
  try {
    draft = reorderSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.reorder", fingerprint);
  if (replay) return replay;

  const unique = new Set(draft.orderedIds);
  if (unique.size !== draft.orderedIds.length)
    return fail(stateInput, context, "validation_error", "A ordem repete um item.");

  if (draft.definitionId) {
    const definition = state.definitions.find((item) => item.id === draft.definitionId);
    if (!definition) return fail(stateInput, context, "not_found", "Classificação não encontrada.");
    const siblings = state.options.filter((item) => item.definitionId === definition.id);
    // A ordem enviada precisa conter a coleção inteira, inclusive arquivadas:
    // posição única e determinística é o que impede empate ao restaurar.
    if (siblings.length !== draft.orderedIds.length || siblings.some((item) => !unique.has(item.id)))
      return fail(stateInput, context, "validation_error", "A ordem não cobre todas as opções.");
    const position = new Map(draft.orderedIds.map((id, index) => [id, index + 1] as const));
    return finish({
      state,
      context,
      command: "classifications.reorder",
      fingerprint,
      targetType: "classification_definition",
      targetId: definition.id,
      value: { definition },
      nextState: {
        ...state,
        options: state.options.map((item) =>
          item.definitionId === definition.id
            ? { ...item, order: position.get(item.id) ?? item.order }
            : item,
        ),
      },
      after: { orderedIds: draft.orderedIds },
      deps,
    });
  }

  if (
    state.definitions.length !== draft.orderedIds.length ||
    state.definitions.some((item) => !unique.has(item.id))
  )
    return fail(stateInput, context, "validation_error", "A ordem não cobre todas as classificações.");
  const position = new Map(draft.orderedIds.map((id, index) => [id, index + 1] as const));
  return finish({
    state,
    context,
    command: "classifications.reorder",
    fingerprint,
    targetType: "classification_definition",
    targetId: draft.orderedIds[0],
    value: {},
    nextState: {
      ...state,
      definitions: state.definitions.map((item) => ({
        ...item,
        order: position.get(item.id) ?? item.order,
      })),
    },
    after: { orderedIds: draft.orderedIds },
    deps,
  });
}

/** Define (ou limpa) o valor de uma classificação em um lead. */
export function executeSetLeadClassification(
  stateInput: ClassificationCommandState,
  input: SetLeadValueInput,
  contextInput: CommandContext,
  deps: ClassificationCommandDeps,
): ClassificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof setLeadValueSchema>;
  try {
    draft = setLeadValueSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "classifications.set_value", fingerprint);
  if (replay) return replay;

  if (state.leadIds && !state.leadIds.includes(draft.leadId))
    return fail(stateInput, context, "not_found", "Lead não encontrado.");
  const definition = state.definitions.find((item) => item.id === draft.definitionId);
  if (!definition) return fail(stateInput, context, "not_found", "Classificação não encontrada.");

  const previous = state.values.find(
    (item) => item.leadId === draft.leadId && item.definitionId === draft.definitionId,
  );

  if (draft.optionId === null) {
    if (!previous) return fail(stateInput, context, "invalid_state", "O lead já está sem valor.");
    return finish({
      state,
      context,
      command: "classifications.set_value",
      fingerprint,
      targetType: "lead_classification_value",
      targetId: `${draft.leadId}:${draft.definitionId}`,
      value: { definition, affectedLeads: 1 },
      nextState: {
        ...state,
        values: state.values.filter(
          (item) => !(item.leadId === draft.leadId && item.definitionId === draft.definitionId),
        ),
      },
      before: { ...previous },
      deps,
    });
  }

  const option = state.options.find(
    (item) => item.id === draft.optionId && item.definitionId === definition.id,
  );
  if (!option) return fail(stateInput, context, "not_found", "Opção não encontrada.");
  // Opção arquivada só continua onde já estava; ela não entra em novo registro.
  if (option.archived && previous?.optionId !== option.id)
    return fail(stateInput, context, "invalid_state", "Esta opção está arquivada.");

  const value: LeadClassificationValue = {
    leadId: draft.leadId,
    definitionId: definition.id,
    optionId: option.id,
  };
  return finish({
    state,
    context,
    command: "classifications.set_value",
    fingerprint,
    targetType: "lead_classification_value",
    targetId: `${value.leadId}:${value.definitionId}`,
    value: { definition, option, value },
    nextState: {
      ...state,
      values: previous
        ? state.values.map((item) =>
            item.leadId === value.leadId && item.definitionId === value.definitionId ? value : item,
          )
        : [...state.values, value],
    },
    before: previous ? { ...previous } : undefined,
    after: { ...value },
    deps,
  });
}

export interface ClassificationDemoAdapter {
  create(input: CreateDefinitionInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  rename(input: RenameDefinitionInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  archive(input: ArchiveDefinitionInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  remove(input: RemoveDefinitionInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  createOption(input: CreateOptionInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  renameOption(input: RenameOptionInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  archiveOption(input: ArchiveOptionInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  removeOption(input: RemoveOptionInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  reorder(input: ReorderInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  setLeadValue(input: SetLeadValueInput, context: CommandContext): Promise<CommandResult<ClassificationCommandValue>>;
  getState(): ClassificationCommandState;
  syncExternal(state: ClassificationCommandState): void;
}

export function createClassificationDemoAdapter(
  initialState: ClassificationCommandState,
  deps: ClassificationCommandDeps,
  onCommit?: (state: ClassificationCommandState) => void,
): ClassificationDemoAdapter {
  let state = cloneState(initialState);
  let queue: Promise<unknown> = Promise.resolve();
  let busy = false;

  const enqueue = (
    operation: () => ClassificationCommandExecution,
  ): Promise<CommandResult<ClassificationCommandValue>> => {
    const run = async () => {
      busy = true;
      try {
        const execution = operation();
        if (execution.result.ok && execution.result.status === "applied") {
          state = execution.state;
          onCommit?.(state);
        }
        return execution.result;
      } finally {
        busy = false;
      }
    };
    if (!busy) {
      const started = run();
      queue = started.then(
        () => undefined,
        () => undefined,
      );
      return started;
    }
    const next = queue.then(run);
    queue = next.then(
      () => undefined,
      () => undefined,
    );
    return next as Promise<CommandResult<ClassificationCommandValue>>;
  };

  return {
    create: (input, context) => enqueue(() => executeCreateClassification(state, input, context, deps)),
    rename: (input, context) => enqueue(() => executeRenameClassification(state, input, context, deps)),
    archive: (input, context) => enqueue(() => executeArchiveClassification(state, input, context, deps)),
    remove: (input, context) => enqueue(() => executeRemoveClassification(state, input, context, deps)),
    createOption: (input, context) =>
      enqueue(() => executeCreateClassificationOption(state, input, context, deps)),
    renameOption: (input, context) =>
      enqueue(() => executeRenameClassificationOption(state, input, context, deps)),
    archiveOption: (input, context) =>
      enqueue(() => executeArchiveClassificationOption(state, input, context, deps)),
    removeOption: (input, context) =>
      enqueue(() => executeRemoveClassificationOption(state, input, context, deps)),
    reorder: (input, context) =>
      enqueue(() => executeReorderClassifications(state, input, context, deps)),
    setLeadValue: (input, context) =>
      enqueue(() => executeSetLeadClassification(state, input, context, deps)),
    getState: () => state,
    syncExternal: (next) => {
      if (!busy) state = cloneState(next);
    },
  };
}
